import { PipelineConfig } from './config-types';
import { LadonOutput, MaskGenerator, WorkerGeneratedResult } from './generator-types';
import { logger } from './logger';
import { workerManager } from './worker-manager';

export class WorkerGenerator implements MaskGenerator {
    private pending: Map<number, (result: LadonOutput) => void> = new Map();
    private lastResult: LadonOutput;
    private newMask = true;

    constructor(public readonly config: PipelineConfig) {
        this.lastResult = {
            data: new Float32Array(
                this.config.mask.outputSize.height * this.config.mask.outputSize.width
            ),
            facesAndLandmarks: { data: new Float32Array(0), dims: [] },
            gesture: new Float32Array(0),
            motion: 0,
            timestamp: 0,
            warm: false,
        };
    }

    public isLoaded(): boolean {
        return !!workerManager.getWorker();
    }

    public async getLadonOutput(): Promise<LadonOutput> {
        const toReturn = {
            data: this.lastResult.data,
            facesAndLandmarks: this.lastResult.facesAndLandmarks,
            gesture: this.lastResult.gesture,
            motion: this.lastResult.motion,
            timestamp: this.lastResult.timestamp,
            warm: this.newMask,
        };

        this.newMask = false;

        return toReturn;
    }

    /**
     * Returns a promise that resolves when the worker returns the mask for the given timestamp.
     * This is useful for synchronizing the processing of frames with their corresponding masks.
     * The promise will be resolved with the mask data when it is available.
     *
     * @param timestamp - The timestamp of the frame for which the mask is requested.
     * @returns A promise that resolves with the mask data when available.
     */
    public async getLadonOutputForTimestamp(timestamp: number): Promise<LadonOutput> {
        return new Promise<LadonOutput>((resolve) => {
            this.pending.set(timestamp, resolve);
        });
    }

    /**
     * Loads the worker and initializes it with the provided configuration.
     * The worker is responsible for processing the input image and generating the mask.
     * Prepares the worker configuration by resolving the final URLs and paths to send to the worker.
     *
     * @returns A promise that resolves when the worker is loaded and initialized.
     */
    public async load(): Promise<void> {
        const config = await workerManager.prepareWorkerConfig(this.config);

        // Listen for worker events
        return new Promise<void>((resolve) => {
            workerManager.onInitialized(() => {
                resolve();
            });
            workerManager.onGenerated((data: WorkerGeneratedResult) => {
                const result: LadonOutput = {
                    data: data.mask,
                    facesAndLandmarks: data.facesAndLandmarks,
                    gesture: data.gesture,
                    motion: data.motion,
                    timestamp: data.timestamp,
                    warm: true,
                };
                this.lastResult = result;
                this.newMask = true;

                // Resolve the pending promise for this timestamp, if any
                const cb = this.pending.get(data.timestamp);
                if (cb) {
                    cb(result);
                    this.pending.delete(data.timestamp);
                }
            });
            workerManager.onDropped((timestamp: number) => {
                // Resolve the pending promise for this timestamp with the last result
                const cb = this.pending.get(timestamp);
                if (cb) {
                    cb(this.lastResult);
                    this.pending.delete(timestamp);
                }
            });
            workerManager.onLog((message, severity) => {
                const msg = `Message received from worker: ${message}`;
                switch (severity) {
                    case 'warn':
                        logger.warn(msg);
                        break;
                    case 'error':
                        logger.error(msg);
                        break;
                    default:
                        logger.log(msg);
                }
            });

            // Initialize the worker if not already done, then always send init
            (async () => {
                await (workerManager.getWorker() ?? workerManager.initializeWorker(config));
                workerManager.sendInit({
                    baseUrl: config.baseUrl,
                    input: config.input,
                    mask: config.mask,
                    wasmUri: config.wasmUri,
                    render: { type: 'passthrough' },
                });
            })();
        });
    }

    /**
     * Sends the input image to the worker for processing.
     * The input image is expected to be a Uint8ClampedArray.
     * Claims ownership of the input tensor's buffer and forwards it to the worker.
     *
     * @param input - The input image as a Uint8ClampedArray.
     * @param timestamp - The timestamp of the input image.
     */
    public async process(input: Uint8ClampedArray, timestamp: number): Promise<void> {
        workerManager.sendGenerate(input, timestamp);
    }

    /**
     * Destroys the worker and cleans up resources.
     * First it sends a message to the worker to destroy itself,
     * then it waits for the worker to respond with a 'destroyed' message.
     * After that, it terminates the worker and revokes the URL if it exists.
     *
     * @returns A promise that resolves when the worker is destroyed.
     */
    public destroy(): Promise<void> {
        return workerManager.destroyWorker();
    }
}
